import { FC, useCallback, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';

import Modal from '@components/Modal/Modal';
import useToggle from '@hooks/useToggle';
import { handleShareInquiryResponse } from '@store/features/inquiries/share/actions';
import {
  inquiriesShareInquiryIdSelector,
  inquiriesShareSelectedSupplementsSelector,
  inquiriesShareUrlIsLoadingSelector,
  inquiriesShareUrlSelector,
} from '@store/features/inquiries/share/selectors';
import { removeSharedInquiryResponseDetails } from '@store/features/inquiries/share/slice';
import useDispatch from '@store/hooks/useDispatch';

import GeneratedLink from './GeneratedLink/GeneratedLink';
import SelectSupplements from './SelectSupplements/SelectSupplements';

import './style.scss';

const ShareResponseModal: FC = () => {
  const { t } = useTranslation();
  const dispatch = useDispatch();

  const [isModalOpen, onModalOpen, onModalClose] = useToggle();

  const shareInquiryId = useSelector(inquiriesShareInquiryIdSelector);
  const shareInquirySupplements = useSelector(inquiriesShareSelectedSupplementsSelector);
  const shortUrl = useSelector(inquiriesShareUrlSelector);
  const isLoading = useSelector(inquiriesShareUrlIsLoadingSelector);

  useEffect(() => {
    shareInquiryId && onModalOpen();
  }, [onModalOpen, shareInquiryId]);

  const handleShare = useCallback(() => {
    shareInquiryId &&
      shareInquirySupplements &&
      dispatch(
        handleShareInquiryResponse({
          inquiryId: shareInquiryId,
          supplementInquiriesIds: shareInquirySupplements,
        }),
      );
  }, [dispatch, shareInquiryId, shareInquirySupplements]);

  const handleExit = useCallback(() => {
    dispatch(removeSharedInquiryResponseDetails());
  }, [dispatch]);

  useEffect(() => () => handleExit(), [handleExit]);

  const modalProps = shortUrl
    ? {
        className: 'share-response-modal share-response-modal_generated-link',
        title: t('shareResponse.generatedLinkTitle'),
        subTitle: t('shareResponse.generatedLinkDescription'),
        onLeftAction: undefined,
        onRightAction: onModalClose,
        leftActionTitle: undefined,
        rightActionTitle: t('common.done'),
      }
    : {
        className: 'share-response-modal share-response-modal_select-supplements',
        title: t('shareResponse.selectSupplementsTitle'),
        subTitle: t('shareResponse.selectSupplementsDescription'),
        onLeftAction: onModalClose,
        onRightAction: handleShare,
        leftActionTitle: t('common.cancel'),
        rightActionTitle: t('common.next'),
        isRightActionDisabled: !shareInquirySupplements?.length,
        isRightActionLoading: isLoading,
      };

  return (
    <Modal
      {...modalProps}
      onClose={onModalClose}
      onExited={handleExit}
      open={isModalOpen}
      contentClass="share-response-modal__body-content"
      icon={<img className="share-response-modal__icon" src="/images/img-weblink.svg" />}
    >
      {shortUrl ? <GeneratedLink /> : <SelectSupplements />}
    </Modal>
  );
};

export default ShareResponseModal;
